import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { type WalletConnectingData } from '@pkg/types';
import { mergeDeduplicatedArrays } from '@pkg/utils';

import { type Account } from '@app/graphql/codegen/graphql';

// TODO: This file/folder may eventually be removed in favour of:
// - `src/store/zustand/wallet > useWalletStore()`
// - Alternatively, we rename this `useConnectWalletSheetStore()`.

// type WalletAddress = `0x${string}`;
type WalletAddress = string;

export interface LegacyWalletData {
    wallets: WalletAddress[];
    // Unique to this hook:
    connecting?: WalletConnectingData;
    disconnecting?: Account;
    showOverview?: boolean;
}

export interface LegacyWalletState extends LegacyWalletData {
    addWallets: (...wallets: WalletAddress[]) => void;
    removeWallets: (...wallets: WalletAddress[]) => void;
    clearWallets: () => void;
    // Unique to this hook:
    setConnecting: (data?: WalletConnectingData) => void;
    setDisconnecting: (data?: Account) => void;
    setShowOverview: (show?: boolean) => void;
    resetShowState: () => void;
    getShowState: () => boolean;
}

const initialState: LegacyWalletData = {
    wallets: [],
    connecting: undefined,
    disconnecting: undefined,
    showOverview: false,
};

export const STORE_KEY_LEGACY_WALLET = 'legacy-wallet-store';

export const useLegacyWalletStore = create<LegacyWalletState>()(
    // TODO: Add `persist()` is we decide to merge this.
    devtools(
        (set, get) => ({
            ...initialState,
            addWallets: (...walletsToAdd) =>
                set((state) => ({
                    // If we want, we can perform an "address validation" step.
                    wallets: mergeDeduplicatedArrays(state.wallets, walletsToAdd),
                })),
            removeWallets: (...walletsToRemove) =>
                set((state) => ({
                    wallets: state.wallets.filter((entry) => !walletsToRemove.includes(entry)),
                })),
            clearWallets: () => set({ wallets: [] }),
            setConnecting: (data) => set({ connecting: data }),
            setDisconnecting: (data) => set({ disconnecting: data }),
            setShowOverview: (show = false) => set({ showOverview: show }),
            resetShowState: () =>
                set({
                    connecting: undefined,
                    disconnecting: undefined,
                    showOverview: false,
                }),
            getShowState: () => {
                return Boolean(get().connecting || get().disconnecting || get().showOverview);
            },
        }),
        { name: STORE_KEY_LEGACY_WALLET },
    ),
);
